import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

type AutoApproveDlnPositionsArguments = {
  autoApproveDlnPositions: boolean;
  companyId: UUID;
};

/**
 * Updates auto-approve DLN positions setting for a given company.
 *
 * @param {string} companyId - The ID of the company to update.
 * @param {boolean} autoApproveDlnPositions - Whether or not to auto-approve DLN positions.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/set_company_auto_approve_dln_positions_company__company_id__auto_approve_dln_positions_put
 */
export const updateCompanyAutoApproveDlnPositions = async (
  companyId: UUID,
  autoApproveDlnPositions: boolean,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.COMPANY.UPDATE_AUTO_APPROVE_DLN_POSITIONS(companyId), {
      searchParams: { auto_approve_dln_positions: autoApproveDlnPositions },
    })
    .json();

  return response;
};

/**
 * Custom hook for updating auto-approve DLN positions setting for a given company.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateCompanyAutoApproveDlnPositions = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, AutoApproveDlnPositionsArguments>({
    mutationFn: async ({
      autoApproveDlnPositions,
      companyId,
    }: AutoApproveDlnPositionsArguments) =>
      updateCompanyAutoApproveDlnPositions(companyId, autoApproveDlnPositions),
    onSuccess(_data, { companyId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAutoApproveDlnPositions(companyId),
      });
    },
  });
};
